import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useQuizeStore } from "../../../stores/quizeStore";
import { useAppStore } from "../../../stores/appStore";
import useUserStore from "../../../stores/userStore";
import {
  containerStyle,
  titleStyle,
  legendContainerStyle,
  legendItemStyle,
  legendBoxStyle,
  legendTextStyle,
  chartWrapperStyle,
} from "./styles";

interface LessonBarChartProps {
  assessmentData?: {
    scoresEarned: number;
    totalMaxScore: number;
    focus: string;
  };
}

export const LessonBarChart = ({
  assessmentData,
}: LessonBarChartProps = {}) => {
  const { focus, domain } = useAppStore();
  const { preassessmentData } = useQuizeStore();
  const getFocusDisplayName = useUserStore((state) => state.getFocusDisplayName);
  const focusDisplayName = getFocusDisplayName(focus, domain);

  const chartData = useMemo(() => {
    // Use assessment data if provided
    if (assessmentData) {
      if (assessmentData.totalMaxScore === 0) return [];

      return [
        {
          name: getFocusDisplayName(assessmentData.focus || focus, domain),
          "Your score": assessmentData.scoresEarned,
          "Max score": assessmentData.totalMaxScore,
        },
      ];
    }

    if (preassessmentData.answered_questions_number === 0) return [];

    const totalUserScore = preassessmentData.scores_earned;
    const totalMaxScore = 5 * preassessmentData.answered_questions_number;

    return [
      {
        name: focusDisplayName,
        "Your score": totalUserScore,
        "Max score": totalMaxScore,
      },
    ];
  }, [
    preassessmentData,
    assessmentData,
    focus,
    domain,
    focusDisplayName,
    getFocusDisplayName,
  ]);

  const xTicks = useMemo(() => {
    const maxScore = assessmentData
      ? assessmentData.totalMaxScore
      : 5 * preassessmentData.answered_questions_number;
    return new Array(6).fill(0).map((_, index) => (maxScore / 5) * index);
  }, [preassessmentData, assessmentData]);

  const maxValue = useMemo(() => {
    if (chartData.length === 0) return 5;
    return Math.max(...chartData.map((item) => item["Max score"]));
  }, [chartData]);

  const displayFocus = getFocusDisplayName(
    assessmentData?.focus || focus,
    domain
  );

  return (
    <Box sx={containerStyle}>
      <Typography sx={titleStyle}>{displayFocus}</Typography>

      <Box sx={legendContainerStyle}>
        <Box sx={{ ...legendItemStyle, ml: "5px" }}>
          <Box
            sx={legendBoxStyle(
              "linear-gradient(90deg, #9056E9 0%, #C39EFF 100%)"
            )}
          />
          <Typography sx={legendTextStyle}>Your score</Typography>
        </Box>
        <Box sx={legendItemStyle}>
          <Box
            sx={legendBoxStyle(
              "linear-gradient(90deg, #66EF64 0%, #66EF6433 100%)"
            )}
          />
          <Typography sx={legendTextStyle}>Max score</Typography>
        </Box>
      </Box>

      {chartData.length === 0 ? (
        <Typography
          sx={{
            fontSize: 12,
            color: "#9CA3AF",
            textAlign: "center",
            mt: "20px",
          }}
        >
          No data to display
        </Typography>
      ) : (
        <Box sx={chartWrapperStyle}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 20, bottom: 10, left: 20 }}
            >
              <defs>
                <linearGradient
                  id="userScoreGradient"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor="#C39EFF" />
                  <stop offset="100%" stopColor="#9056E9" />
                </linearGradient>
                <linearGradient
                  id="maxScoreGradient"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor="#66EF6433" />
                  <stop offset="100%" stopColor="#66EF64" />
                </linearGradient>
              </defs>

              <XAxis
                type="number"
                domain={[0, maxValue]}
                ticks={xTicks}
                tick={{ fill: "#9CA3AF" }}
                axisLine={{ stroke: "#9CA3AF33" }}
                tickLine={{ stroke: "#9CA3AF33" }}
              />
              <YAxis dataKey="name" type="category" hide />

              <Tooltip />
              <Bar
                dataKey="Your score"
                fill="url(#userScoreGradient)"
                barSize={30}
                radius={[0, 8, 8, 0]}
              />
              <Bar
                dataKey="Max score"
                fill="url(#maxScoreGradient)"
                barSize={30}
                radius={[0, 8, 8, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
};
